import ProgressBar from 'react-bootstrap/ProgressBar';
import { NotebookFile } from './hooks/useGetAllFiles';
import { FileProcessingEntry } from './hooks/useFileProcessingStatus';
import { useUploadProgress } from './hooks/uploadProgressStore';
import { useReprocessFile } from './hooks/fileMutations';
import { SharedIcon } from '../SharedIcon/SharedIcon';
import { resolveFileStatus } from './fileStatus';

type FileStatusCellProps = {
  file: NotebookFile;
  ownerId: string;
  entry?: FileProcessingEntry;
  readOnly?: boolean;
};

const badgeClasses = 'badge badge--sm text-dark text-xs font-medium ';

/**
 * The files list's single status column: upload, indexing and Pratus AI
 * readiness.
 */
export function FileStatusCell({
  file,
  ownerId,
  entry,
  readOnly = false,
}: FileStatusCellProps) {
  const uploadPercent = useUploadProgress(file.id);
  const reprocessFile = useReprocessFile();
  const view = resolveFileStatus(file, entry, uploadPercent ?? undefined);
  const showBar = view.progress != null || view.busy;

  if (view.kind === 'skipped') return null;

  return (
    <div className="d-flex flex-column gap-1" style={{ minWidth: '8rem' }}>
      <span className="d-inline-flex align-items-center">
        <span
          className={
            badgeClasses + view.tone + ' d-inline-flex align-items-center'
          }
          title={view.tooltip}
        >
          {view.kind === 'ready' && (
            <SharedIcon iconName="check" size="sm" classes="me-1" />
          )}
          {view.label}
        </span>
        {view.canRetry && !readOnly && (
          <button
            type="button"
            className="button--icon ms-1"
            title="Retry indexing"
            aria-label={`Retry indexing for ${file.file_name}`}
            disabled={reprocessFile.isPending}
            onClick={() =>
              reprocessFile.mutate({ fileId: file.id, workspaceId: ownerId })
            }
          >
            <SharedIcon iconName="refresh" size="sm" />
          </button>
        )}
      </span>
      {showBar && (
        <ProgressBar
          animated={view.busy}
          striped={view.busy}
          now={view.busy ? 100 : (view.progress ?? 0)}
          style={{ height: 6, backgroundColor: 'var(--neutral-400)' }}
          aria-label={view.label}
        />
      )}
    </div>
  );
}
